import React, { useState, useEffect } from 'react';
import ErrorIcon from "../../assets/error.inline.svg";
import StepHeader from './step-header.component';
import FormField from './form-field.component';
import ResultsTable from './results-table.component';
import StatusBadge from './status-badge.component';
import NoResults from './no-results.component';
import NotificationMessage from './notification-message.component';
import { apiClient } from '../../utils/apiClient';

const SolicitorPanelCheckerForm = () => {
  const [step, setStep] = useState(1);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSearchSuccess, setLastSearchSuccess] = useState(false);
  const RECAPTCHA_SITE_KEY = '6LeRCeArAAAAAIkpJqITc_c8fLJdkwD530DDtHGl';
  const RECAPTCHA_ACTION = 'solicitor_panel_check';

  const isSessionValid = () => {
    if (typeof window === 'undefined') return false;
    return !!sessionStorage.getItem('solicitor_panel_auth');
  };

  // Restore step from session after component mounts (client-side only)
  useEffect(() => {
    if (isSessionValid()) {
      setStep(2);
    }
  }, []);

  const [formData, setFormData] = useState(() => {
    if (!isSessionValid()) {
      return {
        fcaReference: '',
        email: '',
        firmName: '',
        solicitorName: '',
        loanAmount: '',
        postcode: '',
        results: []
      };
    }
    
    const auth = sessionStorage.getItem('solicitor_panel_auth');
      
    if (auth) {
      try {
        const authData = JSON.parse(auth);
        return {
          fcaReference: authData.fcaReference || '',
          email: '',
          firmName: authData.firmName || '',
          solicitorName: '',
          loanAmount: '',
          postcode: '',
          results: []
        };
      } catch {
        return {
          fcaReference: '',
          email: '',
          firmName: '',
          solicitorName: '',
          loanAmount: '',
          postcode: '',
          results: []
        };
      }
    }
    
    return {
      fcaReference: '',
      email: '',
      firmName: '',
      solicitorName: '',
      loanAmount: '',
      postcode: '',
      results: []
    };
  });

  const [errors, setErrors] = useState({
    fcaReference: '',
    email: '',
    solicitorName: '',
    loanAmount: '',
    postcode: '',
    firmName: '',
    general: ''
  });

  useEffect(() => {
    const { results, solicitorName, loanAmount, postcode, ...dataToSave } = formData;
    sessionStorage.setItem('solicitor_panel_form', JSON.stringify(dataToSave));
  }, [formData]);

  const numericRegex = /^\d*$/;
  const onReferenceChange = (e) => {
    const newValue = e.target.value;
    if (numericRegex.test(newValue)) {
      setFormData(prev => ({ ...prev, fcaReference: newValue }));
      if (newValue !== '') {
        setErrors(prev => ({ ...prev, fcaReference: '' }));
      }
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value, results: [] }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setLastSearchSuccess(false);
  };

  const authenticateUser = async (fcaRef, email, token) => {
    try {
      const data = await apiClient.get(
        `solicitor/fcacheck?fcaReference=${encodeURIComponent(fcaRef)}&email=${encodeURIComponent(email)}`,
        { headers: { 'Recaptcha-Token': token } }
      );

      if (data.success && data.hasResults) {
        return {
          success: true,
          fcaReference: fcaRef,
          email: email,
          token: token,
          firmName: data.name || 'Authenticated User'
        };
      } else {
        return {
          success: false,
          error: data.message || 'FCA check failed. Please verify your details.'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.data?.message || 'An error occurred during authentication. Please try again.'
      };
    }
  };

  const searchSolicitor = async (data, token) => {
    try {
      const auth = sessionStorage.getItem('solicitor_panel_auth');
      if (!auth) {
        return {
          success: false,
          error: 'Authentication required. Please log in again.'
        };
      }

      const authData = JSON.parse(auth);
      
      // Base64 encode fcaReference:email for header
      const authString = `${authData.fcaReference}:${authData.email}`;
      const base64Auth = btoa(authString);
      
      const result = await apiClient.get(
        `solicitor/search-list?name=${encodeURIComponent(data.solicitorName)}&postCode=${encodeURIComponent(data.postcode)}&loanAmount=${encodeURIComponent(data.loanAmount)}`,
        {
          headers: {
            'FCA_AUTHORIZATION': base64Auth,
            'Recaptcha-Token': token
          }
        }
      );
      
      if (result.success && result.hasResults) {
        const statusMap = {
          'Green': 1,
          'Amber': 2,
          'Red': 3
        };

        const mappedResults = result.items.map(item => ({
          status: statusMap[item.trafficLight] || 3,
          firmName: item.firmName || item.tradingName,
          address: item.address,
          postcode: item.postCode,
          panelNumber: item.lenderPanelNumber
        }));

        return {
          success: true,
          results: mappedResults
        };
      } else {
        return {
          success: result.success,
          hasResults: result.hasResults,
          results: [],
          message: result.message,
          key: result.key
        };
      }
    } catch (error) {
      // Check for HTTP 500 server error
      if (error.status === 500 || error.statusCode === 500) {
        return {
          success: false,
          errorType: 'SERVER_ERROR',
          error: 'Server error occurred'
        };
      }
      return {
        success: false,
        errorType: 'SERVER_ERROR',
        error: error.data?.message || 'An error occurred during the search. Please try again.'
      };
    }
  };

  const validateStep1 = () => {
    let valid = true;
    let newErrors = {
      fcaReference: '',
      email: '',
      solicitorName: '',
      loanAmount: '',
      postcode: '',
      firmName: '',
      general: ''
    };

    if (formData.fcaReference === '') {
      valid = false;
      newErrors.fcaReference = 'This field is mandatory';
    }

    if (formData.email === '') {
      valid = false;
      newErrors.email = 'This field is mandatory';
    } else {
      const emailRegex = /.+@.+\..+/;
      if (!emailRegex.test(formData.email)) {
        valid = false;
        newErrors.email = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const validateStep2 = () => {
    let valid = true;
    let newErrors = {
      fcaReference: '',
      email: '',
      solicitorName: '',
      loanAmount: '',
      postcode: '',
      firmName: '',
      general: ''
    };

    // Loan Amount is always mandatory
    if (!formData.loanAmount || formData.loanAmount.trim() === '') {
      valid = false;
      newErrors.loanAmount = 'This field is mandatory';
    } else if (parseFloat(formData.loanAmount) < 0) {
      // Loan Amount cannot be negative
      valid = false;
      newErrors.loanAmount = 'Loan amount is not a valid number.';
    }

    // At least one of Solicitor Name or Postcode must be provided
    const hasSolicitorName = formData.solicitorName && formData.solicitorName.trim() !== '';
    const hasPostcode = formData.postcode && formData.postcode.trim() !== '';

    if (!hasSolicitorName && !hasPostcode) {
      valid = false;
      newErrors.general = 'VALIDATION_ERROR';
    }

    setErrors(newErrors);
    return valid;
  };

  const onStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      // eslint-disable-next-line no-undef
      const token = await grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION });
      
      const response = await authenticateUser(formData.fcaReference, formData.email, token);

      if (response.success && response.firmName) {
        sessionStorage.setItem('solicitor_panel_auth', JSON.stringify({
          fcaReference: response.fcaReference,
          email: response.email,
          firmName: response.firmName
        }));

        setFormData(prev => ({
          ...prev,
          firmName: response.firmName
        }));
        setStep(2);
      } else {
        setErrors(prev => ({ ...prev, general: response.error || 'Authentication failed' }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, general: 'An error occurred during authentication' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onStep2Submit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setLastSearchSuccess(false);
    setErrors(prev => ({ ...prev, general: '' }));
    setFormData(prev => ({ ...prev, results: [] }));

    try {
      // Generate fresh reCAPTCHA token for search
      // eslint-disable-next-line no-undef
      const token = await grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION });
      
      const response = await searchSolicitor({
        solicitorName: formData.solicitorName,
        loanAmount: formData.loanAmount,
        postcode: formData.postcode
      }, token);

      if (response.errorType === 'SERVER_ERROR') {
        // HTTP 500 - show global error notification
        setFormData(prev => ({
          ...prev,
          results: []
        }));
        setErrors(prev => ({ ...prev, general: 'SERVER_ERROR' }));
        setLastSearchSuccess(false);
        setStep(3);
      } else if (response.success && response.results.length > 0) {
        setFormData(prev => ({
          ...prev,
          results: response.results
        }));
        setLastSearchSuccess(true);
        setStep(3);
      } else if (response.hasResults && (!response.items || response.items.length === 0)) {
        // More than 5 results - hasResults true but items empty
        setFormData(prev => ({
          ...prev,
          results: []
        }));
        setErrors(prev => ({ ...prev, general: 'MULTIPLE_RESULTS' }));
        setLastSearchSuccess(false);
        setStep(3);
      } else if (!response.success && response.message) {
        // Handle validation errors with optional key field for specific input
        setFormData(prev => ({
          ...prev,
          results: []
        }));
        setLastSearchSuccess(false);
        if (response.key && response.key.trim() !== '') {
          // Field-specific error (e.g., key: "solicitorName")
          const fieldName = response.key === 'name' ? 'solicitorName' : response.key;
          setErrors(prev => ({ ...prev, [fieldName]: response.message }));
        } else {
          // Global error - show as notification
          setErrors(prev => ({ ...prev, general: 'API_ERROR', apiMessage: response.message }));
        }
        setStep(3);
      } else {
        // No results but success
        setFormData(prev => ({
          ...prev,
          results: []
        }));
        setLastSearchSuccess(true);
        setStep(3);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, general: 'An error occurred during the search' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const scriptId = 'recaptcha-enterprise-script';
    if (document.getElementById(scriptId)) {
      // eslint-disable-next-line no-undef
      if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        // eslint-disable-next-line no-undef
        grecaptcha.enterprise.ready(() => {
          setRecaptchaLoaded(true);
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // eslint-disable-next-line no-undef
      if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        // eslint-disable-next-line no-undef
        grecaptcha.enterprise.ready(() => {
          setRecaptchaLoaded(true);
        });
      }
    };

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        document.head.removeChild(scriptToRemove);
      }
    };
  }, []);

  const getProgress = () => {
    if (step === 1) return 33;
    if (step === 2 && formData.results.length === 0) return 66;
    return 100;
  };

  const isStep1Valid = formData.fcaReference?.trim() !== '' && formData.email?.trim() !== '';
  const isStep2Valid = formData.loanAmount?.trim() !== '' &&
    (formData.solicitorName?.trim() !== '' || formData.postcode?.trim() !== '');

  const getStatusLabel = (status) => {
    switch (status) {
      case 'green': return 'Approved';
      case 'amber': return 'Requires Check';
      case 'red': return 'Not Approved';
      default: return status;
    }
  };

  return (
    <section className="solicitor-panel-checker-form" data-step={step}>
      {step === 1 && (
        <div className="solicitor-panel-checker-form__step" key="step-1">
          <StepHeader 
            title="Authentication Details" 
            step={step} 
            totalSteps={3} 
            progress={getProgress()} 
          />

          <div className="solicitor-panel-checker-form__content">
            <h4 className="solicitor-panel-checker-form__description">
              Please provide your FCA reference and email address for verification.
            </h4>

          <form className="solicitor-panel-checker-form__form" onSubmit={onStep1Submit}>
            <div className="solicitor-panel-checker-form__row">
              <FormField
                label="FCA reference*"
                id="fcaReference"
                name="fcaReference"
                value={formData.fcaReference}
                onChange={onReferenceChange}
                placeholder="e.g.1313524"
                error={errors.fcaReference}
              />

              <FormField
                label="Email address*"
                id="email"
                name="email"
                value={formData.email}
                onChange={onChange}
                placeholder="e.g.johnsmith@hsbc.co.uk"
                error={errors.email}
                additionalError={errors.general}
              />
            </div>

            <div className="solicitor-panel-checker-form__button-container">
              <button
                type="submit"
                className="solicitor-panel-checker-form__button"
                disabled={!isStep1Valid || isSubmitting}
              >
                {isSubmitting ? 'Loading...' : 'Submit'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {[2,3].includes(step) && (
        <div className="solicitor-panel-checker-form__step" key={`step-${step}`}>
          <StepHeader 
            title="Panel Checker Data" 
            step={step} 
            totalSteps={3}
            progress={getProgress()} 
          />

          <div className="solicitor-panel-checker-form__content">
            <h4 className="solicitor-panel-checker-form__description">
              To check eligibility and generate a result, please enter the total loan amount along with the Solicitor firm name and / or postcode.
            </h4>

            <div className="solicitor-panel-checker-form__info-box">
            <p>
              If the applicant(s) intends to proceed with a Solicitor / Conveyancer that is not on our approved panel, 
              HSBC UK will appoint our own firm to act on a Separate representation basis and the applicant(s) will incur 
              an additional fee. Separate legal representation is not available for: Purchase transactions where the purchase 
              price is £5m for England & Wales and Scotland and £1m for Northern Ireland and all Remortgage and Unencumbered transactions.
            </p>
          </div>

          {errors.firmName !== '' && (
            <div className="solicitor-panel-checker-form__error-message">
              <ErrorIcon /> {errors.firmName}
            </div>
          )}

          {step === 2 && errors.general === 'VALIDATION_ERROR' && (
            <NotificationMessage message="To perform a search, please enter either a Solicitor name or postcode." />
          )}

          <form className="solicitor-panel-checker-form__form" onSubmit={onStep2Submit} key={`form-${step}`}>
            <div className="solicitor-panel-checker-form__row solicitor-panel-checker-form__row--3-cols" key={`row-${step}`}>
              <FormField
                label="Solicitor's Name*"
                id="solicitorName"
                name="solicitorName"
                value={formData.solicitorName}
                onChange={onChange}
                placeholder="e.g.Smith & Partners Law Firm"
                error={errors.solicitorName}
              />

              <FormField
                label="Total loan amount*"
                id="loanAmount"
                name="loanAmount"
                value={formData.loanAmount}
                onChange={onChange}
                placeholder="e.g.£200000"
                error={errors.loanAmount}
              />

              <FormField
                label="Postcode"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={onChange}
                placeholder="e.g.ZZ9 9ZZ"
                error={errors.postcode}
              />
            </div>

            {step === 3 && errors.general === 'MULTIPLE_RESULTS' && (
              <NotificationMessage message="Multiple results found. Please refine your search to include the full firm name or postcode to generate a result." />
            )}

            {step === 3 && errors.general === 'SEARCH_ERROR' && (
              <NotificationMessage message="We are unable to process your request at this time. Please try again later or contact our Broker Support helpdesk for further assistance." />
            )}

            {step === 3 && errors.general === 'SERVER_ERROR' && (
              <NotificationMessage message="We are unable to process your request at this time. Please try again later or contact our Broker Support helpdesk for further assistance." />
            )}

            {step === 3 && errors.general === 'API_ERROR' && (
              <NotificationMessage message={errors.apiMessage} />
            )}
            {step === 3 && formData.results.length === 0 && lastSearchSuccess && !errors.general && !errors.solicitorName && !errors.loanAmount && !errors.postcode && !isSubmitting && (
              <NoResults />
            )}

            {step === 3 && formData.results.length > 0 && (
              <div className="solicitor-panel-checker-form__results">
                <ResultsTable results={formData.results} getStatusLabel={getStatusLabel} />

                <div className="solicitor-panel-checker-form__legend">
                  <div className="solicitor-panel-checker-form__legend-items">
                    <div className="solicitor-panel-checker-form__legend-item solicitor-panel-checker-form__legend-item--success">
                      <StatusBadge status={1} showText={false} />
                      <span><strong>On panel</strong>, can act for HSBC UK</span>
                    </div>
                    <div className="solicitor-panel-checker-form__legend-item solicitor-panel-checker-form__legend-item--warning">
                      <StatusBadge status={2} showText={false} />
                      <span>Separate representation: <strong>On panel</strong> but cannot act for HSBC UK, as the loan amount is greater than the required number of partners. Please input the details of the customer's chosen Solicitor / Conveyancer into the application. Please note that Remortgage applications cannot proceed on a Separate representation basis</span>
                    </div>
                    <div className="solicitor-panel-checker-form__legend-item solicitor-panel-checker-form__legend-item--error">
                      <StatusBadge status={3} showText={false} />
                      <span>Separate representation: <strong>Not on panel</strong> and cannot act for HSBC UK. Please input the details of the customer's chosen Solicitor / Conveyancer into the application. Please note that Remortgage applications cannot proceed on a Separate representation basis.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="solicitor-panel-checker-form__button-container">
              <button
                type="submit"
                className="solicitor-panel-checker-form__button"
                disabled={!isStep2Valid || isSubmitting}
              >
                {isSubmitting ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default SolicitorPanelCheckerForm;
